import {
  Box,
  Container,
  HStack,
  Heading,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import React from "react";
import { FiArrowLeft } from "react-icons/fi";
import { FundCategoryFilter } from "../funds/FundCategoryFilter";
import { FundCard, FundCardSkeleton } from "../funds/FundCard";
import { useFunds } from "../../hooks/useFunds";
import { useFundInvestNavigation } from "../../hooks/useFundInvestNavigation";
import {
  type FundCategoryKey,
  filterFundsByCategory,
  isVisibleInFundCards,
  sortFundsByPriority,
} from "../../config/fundCategories";

export const FundsSection = () => {
  const [selectedCategory, setSelectedCategory] = React.useState<
    FundCategoryKey | "all"
  >("all");
  const navigateToInvest = useFundInvestNavigation();
  const { data, isLoading } = useFunds();
  const bgColor = useColorModeValue("gray.50", "dark.200");
  const textColor = useColorModeValue("gray.800", "white");
  const subtitleColor = useColorModeValue("gray.600", "gray.400");
  const funds = sortFundsByPriority(
    (data?.items || []).filter((fund) =>
      isVisibleInFundCards(fund.fundRegisterCode),
    ),
  );
  const visibleFunds = React.useMemo(
    () => filterFundsByCategory(funds, selectedCategory),
    [funds, selectedCategory],
  );

  return (
    <Box id="funds" py={20} bg={bgColor}>
      <Container maxW="container.xl">
        <VStack spacing={12}>
          <VStack spacing={4} textAlign="center">
            <Heading
              as="h2"
              fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
              color={textColor}
            >
              صندوق‌های سرمایه‌گذاری تامین سرمایه امید
            </Heading>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color={subtitleColor}
              maxW="700px"
            >
              با سرمایه‌گذاری در صندوق‌های متنوع ما، از فرصت‌های بازار بهره‌مند
              شوید
            </Text>
          </VStack>

          {!isLoading && funds.length > 0 ? (
            <FundCategoryFilter
              funds={funds}
              selectedCategory={selectedCategory}
              onChange={setSelectedCategory}
              justify="center"
            />
          ) : null}

          {isLoading ? (
            <VStack w="100%" spacing={6}>
              <HStack>
                <Spinner color="brand.500" />
                <Text color={subtitleColor}>
                  در حال دریافت اطلاعات صندوق‌ها...
                </Text>
              </HStack>
              <SimpleGrid
                columns={{ base: 1, md: 2, lg: 3 }}
                spacing={{ base: 6, md: 8 }}
                w="100%"
                alignItems="stretch"
              >
                {Array.from({ length: 3 }).map((_, index) => (
                  <FundCardSkeleton key={index} />
                ))}
              </SimpleGrid>
            </VStack>
          ) : (
            <SimpleGrid
              columns={{ base: 1, md: 2, lg: 3 }}
              spacing={{ base: 6, md: 8 }}
              w="100%"
              alignItems="stretch"
            >
              {visibleFunds.map((fund, index) => (
                <FundCard
                  key={fund.fundRegisterCode}
                  fund={fund}
                  index={index}
                  primaryAction={{
                    label: "سرمایه‌گذاری",
                    icon: FiArrowLeft,
                    onClick: () => navigateToInvest(fund.fundRegisterCode),
                  }}
                />
              ))}
            </SimpleGrid>
          )}
        </VStack>
      </Container>
    </Box>
  );
};
